import { TRIP_DATES, WEEKDAYS, addDays, fmtLong, fmtTime, hotelForNight, weekdayLong } from "./dates";

const norm = (s) => s.toLowerCase().replace(/[’']/g, "'").replace(/\s+/g, " ").trim();
const CITY_WORDS = { "hong kong": "Hong Kong", hk: "Hong Kong", shenzhen: "Shenzhen", yangshuo: "Yangshuo", guangzhou: "Guangzhou" };

function resolveDate(text, today, trip, hintCity) {
  const t = norm(text);
  if (/\btonight\b|\btoday\b/.test(t)) return today;
  if (/\btomorrow\b/.test(t)) return addDays(today, 1);
  const dm = t.match(/\b(\d{1,2})(?:st|nd|rd|th)?\s*(oct|october)?\b/);
  if (dm && Number(dm[1]) >= 20 && Number(dm[1]) <= 29) return `2026-10-${dm[1].padStart(2, "0")}`;
  const wd = WEEKDAYS.find((w) => t.includes(w.toLowerCase()) || t.includes(w.slice(0, 3).toLowerCase() + " "));
  if (!wd) return null;
  const candidates = TRIP_DATES.filter((d) => weekdayLong(d) === wd);
  if (hintCity) {
    const inCity = candidates.find((d) => trip.days.find((x) => x.date === d)?.city === hintCity);
    if (inCity) return inCity;
  }
  return candidates.find((d) => d >= today) || candidates[0];
}

function resolveTime(text) {
  const t = norm(text);
  const m = t.match(/\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?(?![\d:])/);
  if (!m) return null;
  if (!m[2] && !m[3] && !/\bat\s+\d/.test(t)) return null;
  let h = Number(m[1]); const min = Number(m[2] || 0);
  if (m[3]?.startsWith("p") && h < 12) h += 12;
  if (m[3]?.startsWith("a") && h === 12) h = 0;
  if (!m[3] && h < 12 && /\b(night|evening|tonight|dinner|drinks|pm)\b/.test(t)) h += 12;
  if (h > 23) return null;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

const stripTemporal = (s) => s
  .replace(/\b(on|for|to|this|next)?\s*(monday|tuesday|wednesday|thursday|friday|saturday|sunday|tomorrow|tonight|today)\b/gi, " ")
  .replace(/\b(at\s+)?\d{1,2}(:\d{2})?\s*(am|pm)?\b/gi, " ")
  .replace(/\b(night|evening|morning|afternoon|oct|october|at|the)\b/gi, " ")
  .replace(/\s+/g, " ").trim();

const findEvent = (events, text) => {
  const t = norm(text);
  return events.find((e) => t.includes(norm(e.title))) ||
    events.find((e) => norm(e.title).split(" ").filter((w) => w.length > 3).some((w) => t.includes(w)));
};
const findIdea = (ideas, text) => {
  const t = norm(text);
  return ideas.find((i) => t.includes(norm(i.title).replace(/ at night$/, ""))) ||
    ideas.find((i) => norm(i.title).split(/[\s/–-]+/).filter((w) => w.length > 4).some((w) => t.includes(w)));
};

function describeDay(date, trip) {
  const day = trip.days.find((d) => d.date === date);
  if (!day) return `${fmtLong(date)} isn't part of the trip (20–29 October 2026).`;
  const parts = [`${fmtLong(date)} · ${day.city}.`];
  trip.transports.filter((t) => t.date === date).forEach((t) => parts.push(`${t.title}${t.departTime ? ` ${fmtTime(t.departTime)}` : ""}${t.duration ? ` (${t.duration})` : ""}${t.status === "tbc" ? " — time TBC" : ""}.`));
  const evs = trip.events.filter((e) => e.date === date);
  if (evs.length) parts.push(evs.map((e) => `${e.startTime ? fmtTime(e.startTime) + " " : ""}${e.title}`).join(", ") + ".");
  else if (day.openDay) parts.push("Nothing scheduled yet — an intentionally open day. Ideas can be added from the Ideas tab.");
  const h = hotelForNight(trip.hotels, date);
  parts.push(h ? `Tonight: ${h.name}, ${h.city}.` : date === "2026-10-29" ? "Flight home — details TBC." : "");
  return parts.filter(Boolean).join(" ");
}

export function localAssistant(message, trip, today, ctx = {}) {
  const t = norm(message);
  const cityHint = Object.keys(CITY_WORDS).find((k) => t.includes(k));
  const R = (reply, action = null, extra = {}) => ({ reply, action, source: "local", ...extra });

  if (/what'?s next|what is next|next up/.test(t)) return R("Here's the next item on the itinerary.", { type: "navigate", to: "/?next=1" });

  // Trip spending (log) — distinct from booked costs
  const spending = trip.spending || [];
  const sym = trip.settings.currencySymbol || "$";
  const NICK = { mum: "Mum", mary: "Mum", gucci: "Gucci", angelina: "Gucci", "golden child": "Golden Child", emilio: "Golden Child", boob: "BOOB", romain: "BOOB", romayne: "BOOB", jabbs: "Jabbs", jared: "Jabbs", jab: "Jabbs" };
  const personIn = (s) => Object.keys(NICK).find((p) => s.includes(p)) || (/\b(i|me|my|myself)\b/.test(s) && ctx.profile ? ctx.profile.toLowerCase() : null);
  const cap = (s) => NICK[s] || (Object.values(NICK).find((n) => n.toLowerCase() === s) || s.charAt(0).toUpperCase() + s.slice(1));
  const moneyMatch = t.match(/(?:\$|hk\$|¥|rmb|cny|hkd|aud|usd)?\s?(\d+(?:\.\d{1,2})?)\s*(dollars|hkd|cny|rmb|yuan|aud|usd|\$)?/);
  if (/^(add|log|record)\b/.test(t) && (/(expense|receipt|spent|paid|cost)/.test(t) || (/\$|hkd|cny|rmb|yuan|dollars/.test(t) && moneyMatch))) {
    const amount = moneyMatch ? Number(moneyMatch[1]) : null;
    if (!amount) return R("How much was it? e.g. \"Add $20 dinner paid by Emilio\".");
    const curWord = (t.match(/\b(hkd|cny|rmb|yuan|aud|usd)\b/) || [])[1];
    const currency = curWord ? ({ rmb: "CNY", yuan: "CNY" }[curWord] || curWord.toUpperCase()) : t.includes("¥") ? "CNY" : sym;
    const payerRaw = (t.match(/paid by ([a-z]+(?: child)?)/) || t.match(/\b(mum|mary|gucci|angelina|golden child|emilio|boob|romain|romayne|jabbs|jared|jab)\b/) || [])[1];
    const paidBy = payerRaw ? cap(payerRaw) : ctx.profile || "Mum";
    const category = /dinner|lunch|breakfast|food|drinks|coffee|tea|restaurant|snack/.test(t) ? "Food" : /taxi|train|metro|uber|bus|transport|ferry/.test(t) ? "Transport" : /hotel|room/.test(t) ? "Hotels" : /ticket|tour|show|entry|massage|activity/.test(t) ? "Activities" : /shop|gift|souvenir|market/.test(t) ? "Shopping" : "Other";
    let merchant = t.replace(/^(add|log|record)\b/, "").replace(/\b(an?|the)?\s*(expense|receipt)\b/g, "").replace(/paid by \w+/, "").replace(/(?:\$|hk\$|¥)?\s?\d+(?:\.\d{1,2})?\s*(dollars|hkd|cny|rmb|yuan|aud|usd)?/, "").replace(/\b(for|in|at|on|today|tonight|tomorrow|yesterday)\b/g, " ").replace(/\b(hong kong|shenzhen|yangshuo|guangzhou)\b/g, " ").replace(/\s+/g, " ").trim();
    merchant = merchant ? merchant.charAt(0).toUpperCase() + merchant.slice(1) : category;
    const date = /yesterday/.test(t) ? addDays(today, -1) : resolveDate(t, today, trip) || today;
    const city = cityHint ? CITY_WORDS[cityHint] : trip.days.find((d) => d.date === date)?.city || null;
    return R(`Add ${currency}${amount.toFixed(2)} "${merchant}" paid by ${paidBy} on ${fmtLong(date)}?`, { type: "add_expense", expense: { amount, currency, paidBy, date, city, category, merchant, notes: "" } });
  }
  if (/(spent|spending|expenses?)\b/.test(t) && !/^(add|log)/.test(t)) {
    const p = personIn(t);
    const sumBy = (list) => Object.entries(list.reduce((m, s) => ({ ...m, [s.currency]: (m[s.currency] || 0) + Number(s.amount) }), {})).map(([c, n]) => `${c}${n.toFixed(2)}`).join(" + ") || `${sym}0.00`;
    if (p) { const list = spending.filter((s) => s.paidBy.toLowerCase() === cap(p).toLowerCase()); return R(`${cap(p)} has spent ${sumBy(list)} across ${list.length} expense${list.length === 1 ? "" : "s"}${list.length ? ` — latest: ${list[list.length - 1].merchant}` : ""}.`, { type: "navigate", to: "/more/expenses" }); }
    if (cityHint) { const list = spending.filter((s) => s.city === CITY_WORDS[cityHint]); return R(`${CITY_WORDS[cityHint]} spending: ${sumBy(list)} (${list.length} item${list.length === 1 ? "" : "s"})${list.length ? ": " + list.slice(0, 5).map((s) => `${s.merchant} ${s.currency}${Number(s.amount).toFixed(2)}`).join(", ") : ""}.`, { type: "navigate", to: "/more/expenses" }); }
    return R(`Trip spending so far: ${sumBy(spending)} across ${spending.length} expense${spending.length === 1 ? "" : "s"}. Booked hotel costs from the spreadsheet are separate under Booked / planned costs.`, { type: "navigate", to: "/more/expenses" });
  }

  if (/(who|what).*(owe|owes)/.test(t) || /\bowe\b/.test(t)) {
    const person = trip.expenses.owed.find((o) => t.includes(o.person.toLowerCase()) || (o.person === "Mum" && /\bmary\b/.test(t)));
    if (person) return R(`${person.person} owes ${sym}${person.amount.toFixed(2)} — spreadsheet note: "${person.sourceNote}". Currency isn't specified in the source.`, { type: "navigate", to: "/more/costs" });
    return R(trip.expenses.owed.map((o) => `${o.person}: ${sym}${o.amount.toFixed(2)} ("${o.sourceNote}")`).join(" · "), { type: "navigate", to: "/more/costs" });
  }
  if (/(paid|payment|pay\b)/.test(t)) {
    const unpaid = trip.hotels.filter((h) => h.paymentStatus === "unpaid");
    return R(unpaid.length ? `${unpaid.map((h) => `${h.name} (${h.city})`).join(", ")} is marked NOT PAID in the source spreadsheet. The other hotels don't state a payment status.` : "No hotel is marked unpaid.", { type: "navigate", to: "/more/organiser" });
  }
  if (/(confirm|outstanding|to[- ]?do|tasks|checklist|angelina)/.test(t) && !/^add|^move/.test(t)) {
    const open = trip.tasks.filter((x) => !x.done);
    return R(open.length ? `${open.length} open organiser tasks: ${open.slice(0, 6).map((x) => x.label).join("; ")}${open.length > 6 ? "…" : ""}.` : "Every organiser task is complete.", { type: "navigate", to: "/more/organiser" });
  }

  if (/calendar/.test(t) && !/(what|show|on the calendar|doing)/.test(t)) {
    const ev = findEvent(trip.events, t) || (ctx.lastEventId && trip.events.find((e) => e.id === ctx.lastEventId));
    if (/(whole|entire|full) trip/.test(t)) return R("Exporting the whole trip to your calendar.", { type: "add_to_calendar", target: "trip", title: "Entire trip" });
    if (ev) return R(`Adding "${ev.title}" to your calendar.`, { type: "add_to_calendar", target: "event", id: ev.id, title: ev.title });
    const d = resolveDate(t, today, trip);
    if (d) return R(`Adding ${fmtLong(d)} to your calendar.`, { type: "add_to_calendar", target: "day", id: d, title: fmtLong(d) });
    return R("Which item would you like to add to your calendar? Say e.g. \"add Victoria Peak to my calendar\".");
  }

  if (/(delete|remove|cancel)\b/.test(t)) {
    const ev = findEvent(trip.events, stripTemporal(t.replace(/(delete|remove|cancel)/, "")));
    if (ev) return R(`Remove "${ev.title}" from ${fmtLong(ev.date)}? This needs your confirmation.`, { type: "delete_event", eventId: ev.id, title: ev.title });
    return R("I couldn't find that item on the itinerary.");
  }

  if (/^(move|reschedule|shift)\b/.test(t)) {
    const ev = findEvent(trip.events, t.replace(/^(move|reschedule|shift)/, "").split(/\bto\b/)[0]);
    const toDate = resolveDate(t.split(/\bto\b/).slice(1).join(" to ") || t, today, trip, ev?.city);
    if (ev && toDate) return R(`Move "${ev.title}" to ${fmtLong(toDate)}?`, { type: "move_event", eventId: ev.id, title: ev.title, toDate, startTime: resolveTime(t) || ev.startTime });
    return R(ev ? "Which day should I move it to?" : "I couldn't find that item on the itinerary.");
  }

  if (/^(add|put|schedule|book|plan)\b/.test(t)) {
    const idea = findIdea(trip.ideas, t);
    const date = resolveDate(t, today, trip, idea?.city);
    let title = idea ? idea.title.replace(/ at night$/i, "") : stripTemporal(t.replace(/^(add|put|schedule|book|plan)\b/, "").replace(/\b(to|on|in) (the )?(itinerary|schedule|trip)\b/g, ""));
    title = title.replace(/^(a|an|the)\s+/, "").trim();
    title = title.charAt(0).toUpperCase() + title.slice(1);
    if (!date) return R(`Which day should I add "${title}" to? Try "add ${title} on Wednesday".`);
    const day = trip.days.find((d) => d.date === date);
    if (!day) return R(`${fmtLong(date)} is outside the trip dates (20–29 October).`);
    const category = /dinner|lunch|breakfast|brunch|drinks|food|restaurant|tea/.test(title.toLowerCase()) ? "meal" : idea?.category || "activity";
    const startTime = resolveTime(t);
    return R(`Add "${title}" to ${fmtLong(date)}${startTime ? ` at ${fmtTime(startTime)}` : " (no time set)"}?`, {
      type: "add_event",
      event: { title, date, startTime, endTime: null, city: day.city, category, status: "planned", description: "", notes: "", ideaId: idea?.id || null },
    });
  }

  if (/(address|chinese|driver|taxi)/.test(t)) {
    const byName = trip.hotels.find((h) => t.includes(h.name.toLowerCase().split(" ")[0]));
    const date = resolveDate(t, today, trip) || today;
    const h = byName || (cityHint && trip.hotels.find((x) => x.city === CITY_WORDS[cityHint])) || hotelForNight(trip.hotels, date) || trip.hotels[0];
    return R(`${h.name}: ${h.addressChinese} — ${h.address}. Opening the driver card.`, { type: "show_driver", hotelId: h.id });
  }

  if (/(stay|hotel|sleep)/.test(t)) {
    const date = resolveDate(t, today, trip) || today;
    const h = (cityHint && trip.hotels.find((x) => x.city === CITY_WORDS[cityHint])) || hotelForNight(trip.hotels, date);
    if (h) return R(`${cityHint ? `In ${h.city}` : `On ${fmtLong(date)}`} you're staying at ${h.name}, ${h.city} (${h.checkIn.slice(8)}–${h.checkOut.slice(8)} Oct, ${h.nights} nights). ${h.address}.`, { type: "navigate", to: "/more/stays" });
    if (date < "2026-10-20") return R(`The trip hasn't started yet. First night (20 Oct) is at AKVO, Hong Kong.`, { type: "navigate", to: "/more/stays" });
    return R(`No hotel is booked for ${fmtLong(date)} — that's the flight home.`);
  }

  if (/(train|flight|plane|fly)/.test(t)) {
    const tr = trip.transports.find((x) => cityHint && x.toCity.toLowerCase() === CITY_WORDS[cityHint].toLowerCase()) ||
      trip.transports.find((x) => /home/.test(t) && x.id === "tr_flight_home") ||
      trip.transports.find((x) => /train/.test(t) && x.type === "train") || trip.transports[0];
    const time = tr.departTime ? `${fmtTime(tr.departTime)}${tr.arriveTime ? ` → ${fmtTime(tr.arriveTime)}` : ""}` : "time TBC";
    return R(`${tr.title} · ${fmtLong(tr.date)} · ${time}${tr.duration ? ` · ${tr.duration}` : ""}.${tr.missing?.length ? ` Still to confirm: ${tr.missing.join(", ")}.` : ""}`, { type: "navigate", to: "/more/transport" });
  }

  if (/(doing|plan|schedule|happening|itinerary|calendar|what.*(today|tomorrow|on))/.test(t)) {
    const date = resolveDate(t, today, trip, cityHint && CITY_WORDS[cityHint]);
    if (!date && cityHint) {
      const dates = trip.days.filter((d) => d.city === CITY_WORDS[cityHint]).map((d) => d.date);
      return R(dates.map((d) => describeDay(d, trip)).join("\n\n"), { type: "navigate", to: `/trip?date=${dates[0]}` });
    }
    const d = date || today;
    if (d < "2026-10-20") return R(`The adventure begins ${fmtLong("2026-10-20")}: ${describeDay("2026-10-20", trip)}`, { type: "navigate", to: "/trip?date=2026-10-20" });
    return R(describeDay(d, trip), { type: "navigate", to: `/trip?date=${d}` });
  }

  return R("I can tell you what's planned on any day, where you're staying, train and flight details, addresses in Chinese, what's owed, or add and move items — e.g. \"Add Canton Tower Tuesday at 7:30\".");
}
